/**
 * zod schemas shared by web and mobile forms. Numeric fields are entered as
 * Turkish-formatted text and validated with parseNumber(); messages are i18n keys.
 */
import { z } from 'zod';
import { gtinOk } from '../items';
import { parseNumber } from '../money';
import { isValidIban } from '../text';

const required = 'validation.required';

export const decimalText = (opts: { min?: number; allowZero?: boolean; allowNegative?: boolean } = {}) =>
  z
    .string()
    .trim()
    .min(1, { message: required })
    .refine((v) => parseNumber(v) !== null, { message: 'validation.number' })
    .refine(
      (v) => {
        const d = parseNumber(v);
        if (!d) return false;
        if (!opts.allowNegative && d.isNegative()) return false;
        if (!opts.allowZero && d.isZero()) return false;
        if (opts.min !== undefined && d.lessThan(opts.min)) return false;
        return true;
      },
      { message: 'validation.positive' },
    );

export const optionalDecimalText = z
  .string()
  .trim()
  .optional()
  .refine((v) => !v || parseNumber(v) !== null, { message: 'validation.number' });

export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'validation.date' });
export const uuid = z.uuid({ message: 'validation.required' });
export const currency = z.string().regex(/^[A-Z]{3}$/, { message: 'validation.currency' });

// ── auth ─────────────────────────────────────────────────────────────────────
export const MIN_PASSWORD_LENGTH = 12;

export const passwordLoginSchema = z.object({
  email: z.email({ message: 'validation.email' }),
  password: z.string().min(1, { message: required }),
  remember: z.boolean(),
});
export type PasswordLoginInput = z.infer<typeof passwordLoginSchema>;

export const magicLinkSchema = z.object({ email: z.email({ message: 'validation.email' }) });

export const setPasswordSchema = z.object({
  password: z.string().min(MIN_PASSWORD_LENGTH, { message: 'validation.passwordLength' }),
  displayName: z.string().trim().min(2, { message: required }),
});

/** 0–4 password strength for the meter under "Şifre belirle". */
export function passwordStrength(pw: string): 0 | 1 | 2 | 3 | 4 {
  let s = 0;
  if (pw.length >= MIN_PASSWORD_LENGTH) s++;
  if (/[a-zçğıöşü]/.test(pw) && /[A-ZÇĞİÖŞÜ]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9çğıöşüÇĞİÖŞÜ]/.test(pw) || pw.length >= 20) s++;
  return (pw.length < 8 ? Math.min(s, 1) : s) as 0 | 1 | 2 | 3 | 4;
}

// ── onboarding ───────────────────────────────────────────────────────────────
export const companyStepSchema = z.object({
  legalName: z.string().trim().min(2, { message: required }),
  brand: z.string().trim().optional(),
  taxNo: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || /^\d{10,11}$/.test(v), { message: 'validation.taxNo' }),
  taxOffice: z.string().trim().optional(),
  reportingCurrency: z.enum(['TRY', 'USD', 'EUR']),
  autoFx: z.boolean(),
});
export type CompanyStepInput = z.infer<typeof companyStepSchema>;

export const warehouseRowSchema = z.object({
  id: z.string().optional(),
  code: z.string().trim().min(1, { message: required }).max(12),
  name: z.string().trim().min(1, { message: required }),
  locations: z.number().int().min(0).max(999),
});
export const warehousesStepSchema = z.object({
  own: z.array(warehouseRowSchema).min(1, { message: 'onboarding.warehouses.needOne' }),
  marketplaces: z.array(z.string()),
});

export const packagingStepSchema = z.object({
  packQty: z.number().int().positive({ message: 'validation.positive' }),
  casePacks: z.number().int().positive({ message: 'validation.positive' }),
  napkinWeightG: decimalText(),
});

// ── accounting ───────────────────────────────────────────────────────────────
export const contactSchema = z.object({
  type: z.enum(['customer', 'supplier', 'both', 'marketplace', 'partner', 'other']),
  name: z.string().trim().min(2, { message: required }),
  taxNo: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || /^[A-Z0-9-]{5,20}$/i.test(v), { message: 'validation.taxNo' }),
  taxOffice: z.string().trim().optional(),
  country: z.string().trim().length(2),
  city: z.string().trim().optional(),
  currency,
  paymentTermsDays: z.number().int().min(0).max(365),
  /** W-26 künye "Risk limiti"; empty means no limit is tracked */
  riskLimit: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || parseNumber(v)?.isNegative() === false, { message: 'validation.positive' }),
  email: z.union([z.literal(''), z.email({ message: 'validation.email' })]).optional(),
  phone: z.string().trim().optional(),
  iban: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || isValidIban(v), { message: 'validation.iban' }),
  notes: z.string().optional(),
});
export type ContactInput = z.infer<typeof contactSchema>;

export const cashAccountSchema = z.object({
  name: z.string().trim().min(2, { message: required }),
  type: z.enum(['cash', 'bank', 'payment_provider']),
  currency,
  openingBalance: decimalText({ allowZero: true, allowNegative: true }),
  openingDate: isoDate,
  bankName: z.string().trim().optional(),
  iban: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || isValidIban(v), { message: 'validation.iban' }),
});
export type CashAccountInput = z.infer<typeof cashAccountSchema>;

export const transactionSchema = z
  .object({
    kind: z.enum(['receipt', 'payment', 'transfer', 'expense', 'income', 'fx_diff']),
    accountId: uuid,
    date: isoDate,
    amount: decimalText({ allowZero: false }),
    fxRate: optionalDecimalText,
    contactId: z.string().optional(),
    categoryId: z.string().optional(),
    transferAccountId: z.string().optional(),
    transferAmount: optionalDecimalText,
    description: z.string().trim().max(500).optional(),
    vatRateId: z.string().optional(),
    vatAmount: optionalDecimalText,
    fxDirection: z.enum(['in', 'out']).optional(),
    allocations: z.array(z.object({ invoiceId: z.string(), amount: z.string(), accountAmount: z.string().optional() })).optional(),
  })
  .superRefine((v, ctx) => {
    if ((v.kind === 'expense' || v.kind === 'income') && !v.categoryId)
      ctx.addIssue({ code: 'custom', path: ['categoryId'], message: 'validation.category' });
    if (v.kind === 'transfer' && !v.transferAccountId)
      ctx.addIssue({ code: 'custom', path: ['transferAccountId'], message: 'validation.targetAccount' });
    if (v.kind === 'transfer' && v.transferAccountId === v.accountId)
      ctx.addIssue({ code: 'custom', path: ['transferAccountId'], message: 'validation.sameAccount' });
    if ((v.kind === 'receipt' || v.kind === 'payment') && !v.contactId)
      ctx.addIssue({ code: 'custom', path: ['contactId'], message: 'validation.contact' });
  });
export type TransactionInput = z.infer<typeof transactionSchema>;

export const invoiceLineSchema = z.object({
  description: z.string().trim().min(1, { message: required }),
  qty: decimalText(),
  unit: z.string().min(1),
  unitPrice: decimalText({ allowZero: true }),
  discountPct: optionalDecimalText,
  taxRateId: z.string().optional(),
  taxPct: z.string().optional(),
});

export const invoiceSchema = z.object({
  type: z.enum(['purchase', 'sale']),
  contactId: uuid,
  no: z.string().trim().optional(),
  date: isoDate,
  dueDate: isoDate.optional().or(z.literal('')),
  currency,
  fxRate: optionalDecimalText,
  notes: z.string().optional(),
  lines: z.array(invoiceLineSchema).min(1, { message: 'accounting.invoices.needLine' }),
});
export type InvoiceInput = z.infer<typeof invoiceSchema>;

/** Mobile M-12 "Masraf ekle". */
export const expenseSchema = z.object({
  accountId: uuid,
  amount: decimalText(),
  categoryId: uuid,
  note: z.string().trim().max(500).optional(),
  splitVat: z.boolean(),
  vatRateId: z.string().optional(),
  date: isoDate,
});
export type ExpenseInput = z.infer<typeof expenseSchema>;

// ── items (W-04 / W-05 §2.3) ─────────────────────────────────────────────────
/** A number that may arrive as a JSON number or as text typed into an import. */
const attrNumber = z.union([z.number(), z.string()]).transform((v, ctx) => {
  const d = typeof v === 'number' ? v : Number(parseNumber(v)?.toString() ?? NaN);
  if (!Number.isFinite(d)) {
    ctx.addIssue({ code: 'custom', message: 'validation.number' });
    return z.NEVER;
  }
  return d;
});

const positiveAttr = attrNumber.refine((v) => v > 0, { message: 'validation.positive' });

/**
 * `items.attributes` — the JSON the item form writes and the product screens
 * read (W-04/W-05 §2.3). Unknown keys survive untouched: an import, a later
 * phase or the Amazon mapping may add its own, and a save must not drop them.
 *
 * `colour` holds the palette **key** (decision C1); `chip_hex` overrides the
 * chip for a colour outside the palette.
 */
export const itemAttributesSchema = z
  .object({
    /** Open size in cm, napkins: { w: 40, h: 40 } */
    size_cm: z.object({ w: positiveAttr, h: positiveAttr }).optional(),
    fold: z.enum(['1/4', '1/6', '1/8', 'pocket', 'flat']).optional(),
    format: z.enum(['dinner', 'cocktail', 'guest_towel']).optional(),
    colour: z.string().trim().min(1).optional(),
    chip_hex: z
      .string()
      .trim()
      .regex(/^#[0-9A-Fa-f]{6}$/, { message: 'validation.colourHex' })
      .optional(),
    print_type: z.enum(['none', 'foil', 'digital', 'foil_digital']).optional(),
    print_design: z.string().trim().optional(),
    /** g/m² */
    gsm: positiveAttr.optional(),
    /** Overrides the napkinWeightG formula when the real weight is known (8,33 g). */
    unit_weight_g: positiveAttr.optional(),
    /** Bobbin width in cm. */
    roll_width_cm: positiveAttr.optional(),
    /** Carton outer dimensions in cm. */
    dims_cm: z.object({ l: positiveAttr, w: positiveAttr, h: positiveAttr }).optional(),
    /** Foil width in mm. */
    width_mm: positiveAttr.optional(),
    material: z.string().trim().optional(),
    certifications: z.array(z.string().trim().min(1)).optional(),
  })
  .catchall(z.unknown());
export type ItemAttributes = z.infer<typeof itemAttributesSchema>;

export const inviteSchema = z.object({
  email: z.email({ message: 'validation.email' }),
  displayName: z.string().trim().min(2, { message: required }),
  role: z.enum(['partner', 'operator', 'accountant_readonly', 'guest']),
});
export type InviteInput = z.infer<typeof inviteSchema>;

/**
 * The W-04b item form (drawer). Numeric fields are Turkish-formatted text like
 * every other form in the app; the screen converts them with `parseNumber` when
 * it builds the `save_item` payload, so nothing here is a float.
 *
 * What is *not* validated here is what only the database can know: SKU and
 * barcode uniqueness, whether the base unit may still change, and whether a
 * unit exists for the company. Those come back as Turkish errors from the RPC.
 */
const optionalInt = z
  .string()
  .trim()
  .optional()
  .refine((v) => !v || /^\d+$/.test(v.replace(/\./g, '')), { message: 'validation.integer' });

export const itemUomFormSchema = z.object({
  unit: z.string().trim().min(1, { message: required }),
  /** Base-unit amount of one such unit; the base row itself is forced to 1 by `save_item`. */
  factorToBase: decimalText(),
  isPurchaseDefault: z.boolean().optional(),
  isSalesDefault: z.boolean().optional(),
  isOrderMultiple: z.boolean().optional(),
  isCountable: z.boolean().optional(),
  lengthCm: optionalDecimalText,
  widthCm: optionalDecimalText,
  heightCm: optionalDecimalText,
  grossWeightKg: optionalDecimalText,
});
export type ItemUomFormInput = z.infer<typeof itemUomFormSchema>;

export const itemFormSchema = z
  .object({
    sku: z
      .string()
      .trim()
      .min(2, { message: required })
      .regex(/^[A-Z0-9][A-Z0-9._-]{1,39}$/, { message: 'validation.sku' }),
    nameTr: z.string().trim().min(2, { message: required }),
    nameEn: z.string().trim().optional(),
    shortName: z.string().trim().optional(),
    type: z.enum(['raw_material', 'semi_finished', 'finished_good', 'packaging', 'consumable', 'service']),
    status: z.enum(['draft', 'active', 'inactive']),
    itemGroupId: z.string().optional(),
    barcode: z.string().trim().optional(),
    baseUnit: z.string().trim().min(1, { message: required }),
    stockUnit: z.string().trim().optional(),
    uomPreset: z.enum(['napkin', 'bobbin', 'none']).optional(),
    uoms: z.array(itemUomFormSchema),
    /**
     * "Birimler ve paketleme" — the packaging chain as the form asks for it
     * (adet/paket · paket/koli · koli/palet). `napkinUomRows()` turns the three
     * into the UoM ladder; the defaults come from `settings.packaging`.
     */
    packQty: optionalDecimalText,
    casePacks: optionalDecimalText,
    palletCases: optionalDecimalText,
    // attributes (§2.3) — each one optional, because a carton has none of a napkin's
    sizeW: optionalDecimalText,
    sizeH: optionalDecimalText,
    fold: z.string().optional(),
    format: z.string().optional(),
    colour: z.string().optional(),
    chipHex: z
      .string()
      .trim()
      .optional()
      .refine((v) => !v || /^#[0-9A-Fa-f]{6}$/.test(v), { message: 'validation.colourHex' }),
    printType: z.string().optional(),
    printDesign: z.string().trim().optional(),
    gsm: optionalDecimalText,
    unitWeightG: optionalDecimalText,
    rollWidthCm: optionalDecimalText,
    widthMm: optionalDecimalText,
    dimsL: optionalDecimalText,
    dimsW: optionalDecimalText,
    dimsH: optionalDecimalText,
    material: z.string().trim().optional(),
    certifications: z.string().trim().optional(),
    // planning
    reorderPoint: optionalDecimalText,
    reorderQty: optionalDecimalText,
    safetyStockDays: optionalInt,
    leadTimeDays: optionalInt,
    minBatchQty: optionalDecimalText,
    isLotTracked: z.boolean(),
    shelfLifeDays: optionalInt,
    defaultSupplierId: z.string().optional(),
    hsCode: z
      .string()
      .trim()
      .optional()
      .refine((v) => !v || /^\d{4}(\.\d{2}){0,3}$/.test(v), { message: 'validation.hsCode' }),
    countryOfOrigin: z.string().optional(),
    notes: z.string().trim().optional(),
    /** Bought items only, entered in the supplier's currency (E14). */
    standardCost: optionalDecimalText,
    standardCostCurrency: z.string().optional(),
    standardCostRate: optionalDecimalText,
  })
  .superRefine((v, ctx) => {
    // a size is either complete or absent: "40 × —" would be filed as a real value
    if (!!v.sizeW !== !!v.sizeH) {
      ctx.addIssue({ code: 'custom', path: [v.sizeW ? 'sizeH' : 'sizeW'], message: required });
    }
    // the same check digit `private.gtin_ok` applies, so a barcode the database
    // would refuse never leaves the drawer
    if (v.barcode && !gtinOk(v.barcode)) {
      ctx.addIssue({ code: 'custom', path: ['barcode'], message: 'validation.gtin' });
    }
    if (v.standardCost && v.standardCostCurrency && v.standardCostCurrency !== 'TRY' && !v.standardCostRate) {
      ctx.addIssue({ code: 'custom', path: ['standardCostRate'], message: required });
    }
  });
export type ItemFormInput = z.infer<typeof itemFormSchema>;

// ── stock movement (W-07 §4.5.6) ─────────────────────────────────────────────
/**
 * What the "Yeni stok hareketi" drawer must have before it may be sent. The
 * shape rules mirror `private.movement_before`: Çıkış and Transfer need a source,
 * Giriş and Transfer a target, Düzeltme exactly one of the two, and the two may
 * never be the same location. Everything the server alone can know — whether the
 * balance covers the quantity, whether the reason is allowed for the role — is
 * checked by the engine and shown as a field error when it refuses.
 */
export const stockMovementFormSchema = z
  .object({
    kind: z.enum(['in', 'out', 'transfer', 'adjust']),
    itemId: z.string().min(1, { message: 'stock.form.errors.item' }),
    lotId: z.string().optional(),
    /** "+ Yeni lot": the engine numbers it, the form only carries the extra facts. */
    newLot: z.boolean().optional(),
    isLotTracked: z.boolean().optional(),
    qty: decimalText(),
    unit: z.string().min(1, { message: required }),
    fromLocationId: z.string().optional(),
    toLocationId: z.string().optional(),
    reasonCodeId: z.string().min(1, { message: 'stock.form.errors.reason' }),
    requiresNote: z.boolean().optional(),
    requiresPhoto: z.boolean().optional(),
    note: z.string().trim().max(500, { message: 'stock.form.errors.noteLong' }).optional(),
    photoCount: z.number().int().min(0).optional(),
    /** G5, inbound only and only for a role that sees costs. */
    unitCost: optionalDecimalText,
    unitCostCurrency: z.string().optional(),
    unitCostRate: optionalDecimalText,
    supplierId: z.string().optional(),
    supplierLotNo: z.string().trim().optional(),
    expiryDate: z.string().trim().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.isLotTracked && !v.lotId && !v.newLot) ctx.addIssue({ code: 'custom', path: ['lotId'], message: 'stock.form.errors.lot' });
    if ((v.kind === 'out' || v.kind === 'transfer') && !v.fromLocationId) {
      ctx.addIssue({ code: 'custom', path: ['fromLocationId'], message: 'stock.form.errors.from' });
    }
    if ((v.kind === 'in' || v.kind === 'transfer') && !v.toLocationId) {
      ctx.addIssue({ code: 'custom', path: ['toLocationId'], message: 'stock.form.errors.to' });
    }
    if (v.kind === 'adjust' && !v.fromLocationId && !v.toLocationId) {
      ctx.addIssue({ code: 'custom', path: ['fromLocationId'], message: 'stock.form.errors.location' });
    }
    if (v.fromLocationId && v.fromLocationId === v.toLocationId) {
      ctx.addIssue({ code: 'custom', path: ['toLocationId'], message: 'stock.form.errors.sameLocation' });
    }
    if (v.requiresNote && !v.note) ctx.addIssue({ code: 'custom', path: ['note'], message: 'stock.form.errors.noteRequired' });
    if (v.requiresPhoto && !v.photoCount) ctx.addIssue({ code: 'custom', path: ['photoCount'], message: 'stock.form.errors.photoRequired' });
    // a foreign-currency unit cost cannot be booked without the rate it was agreed at
    if (v.unitCost && v.unitCostCurrency && v.unitCostCurrency !== 'TRY' && !v.unitCostRate) {
      ctx.addIssue({ code: 'custom', path: ['unitCostRate'], message: required });
    }
  });
export type StockMovementFormInput = z.infer<typeof stockMovementFormSchema>;
